import { applySortModel, serializeFilterModelEntry } from './filterUtils';
import { applyStoreLocationIdsFromFilterModel } from './storeLocationFilter';
import { isBlockEntirelyBeforeCurrentPage } from './ssrmPaginationGuard';

function isGridApiAlive(api) {
  if (!api) return false;
  try {
    if (typeof api.isDestroyed === 'function' && api.isDestroyed()) return false;
  } catch (e) {
    return false;
  }
  return true;
}

/** Current pager page/size for SSRM off-target block detection. */
function readPaginationSnapshot(api) {
  if (!isGridApiAlive(api)) return null;
  const safe = (fn, fallback) => {
    try {
      if (!isGridApiAlive(api)) return fallback;
      return typeof fn === 'function' ? fn.call(api) : fallback;
    } catch (e) {
      return fallback;
    }
  };
  return {
    currentPage: safe(api.paginationGetCurrentPage, null),
    pageSize: safe(api.paginationGetPageSize, null),
  };
}

const MAX_FETCH_RETRIES = 3;
const FETCH_TIMEOUT_MS = 90000;

const fetchPageWithRetry = (fetchPage, requestBody) => {
  let attempt = 0;

  const run = () => {
    let timeoutId = null;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('Report request timed out')),
        FETCH_TIMEOUT_MS
      );
    });

    return Promise.race([fetchPage(requestBody), timeoutPromise])
      .then((response) => {
        if (timeoutId) clearTimeout(timeoutId);
        return response;
      })
      .catch((error) => {
        if (timeoutId) clearTimeout(timeoutId);
        attempt += 1;
        if (attempt < MAX_FETCH_RETRIES) {
          return run();
        }
        throw error;
      });
  };

  return run();
};

/**
 * Normalize common API list payloads into { results, total }.
 */
export function normalizeServerSidePage(response) {
  if (response == null) {
    return { results: [], total: 0 };
  }
  if (Array.isArray(response)) {
    return { results: response, total: response.length };
  }

  // Explicit failure payloads
  if (response.error && !response.results && !response.data && !response.allVendorsDetails) {
    return { error: response };
  }
  // Express handlers often res.send(JSON.stringify({ message, stack }))
  if (
    response.message &&
    response.stack &&
    !response.results &&
    !response.Results &&
    !response.data &&
    !response.rows
  ) {
    return { error: response };
  }

  const nested = response.data && typeof response.data === 'object' ? response.data : null;

  const results =
    response.results ||
    response.Results ||
    response.rows ||
    response.allVendorsDetails ||
    (nested && (nested.results || nested.Results || nested.rows || nested.allVendorsDetails)) ||
    (Array.isArray(response.data) ? response.data : null) ||
    [];

  const totalRaw =
    response.total != null
      ? response.total
      : response.Total != null
        ? response.Total
      : nested && nested.total != null
        ? nested.total
        : nested && nested.Total != null
          ? nested.Total
        : Array.isArray(results)
          ? results.length
          : 0;

  return {
    results: Array.isArray(results) ? results : [],
    total: Number(totalRaw) || 0,
  };
}

/**
 * Creates an AG Grid server-side datasource.
 *
 * @param {object} options
 * @param {function} options.fetchPage - async (body) => { results, total } or raw API JSON
 * @param {object} options.filterBody - mutable request body (pageNo, pageSize, sort*, searchObject)
 * @param {function} [options.mapFilterModel] - (filterModel) => searchObject
 * @param {object} [options.sortMap] - colId -> { tableName, sortColumn }
 * @param {object} [options.tableAliases] - table name -> SQL alias (e.g. prescriptions_master -> pm)
 * @param {boolean} [options.pageZeroBased=true] - pharmacy APIs use 0-based pageNo
 * @param {function} [options.onSuccess] - (results, total) => void
 * @param {function} [options.onError] - (error) => void
 * @param {function} [options.getPageSize] - () => number (defaults to filterBody.pageSize)
 */
export const createServerSideDatasource = ({
  fetchPage,
  filterBody,
  mapFilterModel,
  applyFilterModel,
  sortMap,
  tableAliases,
  pageZeroBased = true,
  onSuccess,
  onError,
  getPageSize,
}) => {
  let requestSeq = 0;
  let cacheGeneration = 0;
  const inflightBlocks = new Map();
  const blockCache = new Map();
  // Dedupe viewport nudges when LazyStore storms many off-target getRows.
  let pageNudgeTimer = null;
  let pageNudgeTarget = null;

  const scheduleCurrentPageNudge = (api, targetRow) => {
    if (
      !isGridApiAlive(api) ||
      typeof api.ensureIndexVisible !== 'function' ||
      targetRow == null ||
      targetRow < 0
    ) {
      return;
    }
    if (pageNudgeTimer != null && pageNudgeTarget === targetRow) {
      return;
    }
    if (pageNudgeTimer != null) {
      clearTimeout(pageNudgeTimer);
    }
    pageNudgeTarget = targetRow;
    pageNudgeTimer = setTimeout(() => {
      pageNudgeTimer = null;
      const target = pageNudgeTarget;
      pageNudgeTarget = null;
      if (!isGridApiAlive(api) || target == null) return;
      try {
        api.ensureIndexVisible(target, 'top');
      } catch (e) {
        // Grid may be mid-teardown.
      }
    }, 0);
  };

  const blockKey = (request, body) => {
    const start = request && request.startRow != null ? request.startRow : 0;
    const end = request && request.endRow != null ? request.endRow : start;
    const sort =
      request && request.sortModel && request.sortModel[0]
        ? `${request.sortModel[0].colId}:${request.sortModel[0].sort}`
        : '';
    const fm = (request && request.filterModel) || {};
    const filterSig = Object.keys(fm)
      .sort()
      .map((k) => `${k}:${serializeFilterModelEntry(fm[k])}`)
      .join('|');
    const ids = body || {};
    return [
      start,
      end,
      sort,
      filterSig,
      ids.fromdate || '',
      ids.todate || '',
      ids.searchtext || '',
      ids.search || '',
      ids.box_id || '',
      (ids.store_ids || []).join(','),
      (ids.box_ids || []).join(','),
      ids.pageSize || '',
      (Array.isArray(ids.taskTypeList) ? ids.taskTypeList.join(',') : ''),
      (Array.isArray(ids.statusList) ? ids.statusList.join(',') : ''),
      ids.guestOnly ? '1' : '0',
      ids.__lane || '',
    ].join(':');
  };

  const notifySuccess = (p, rowData, total) => {
    try {
      if (typeof p.success === 'function') {
        p.success({ rowData, rowCount: total });
        return;
      }
      if (typeof p.successCallback === 'function') {
        p.successCallback(rowData, total);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const notifyFail = (p) => {
    if (typeof p.fail === 'function') {
      p.fail();
    } else if (typeof p.failCallback === 'function') {
      p.failCallback();
    }
  };

  return {
    invalidateCache() {
      cacheGeneration += 1;
      blockCache.clear();
      inflightBlocks.clear();
      if (pageNudgeTimer != null) clearTimeout(pageNudgeTimer);
      pageNudgeTimer = null;
      pageNudgeTarget = null;
    },
    getRows(params) {
      const generation = cacheGeneration;
      const key = blockKey(params.request, filterBody);
      const req = (params && params.request) || {};
      const startRow = req.startRow != null ? req.startRow : 0;
      const endRow = req.endRow != null ? req.endRow : startRow;
      const pager = readPaginationSnapshot(params && params.api);
      const pageSizeForPager =
        (pager && pager.pageSize) ||
        (getPageSize && getPageSize()) ||
        filterBody.pageSize ||
        10;

      // Reject LazyStore walks below the visible page after sparse jumps
      // (Last → Previous). Prefer fail() over empty success — empty success
      // can shrink SSRM rowCount. Do not purge-on-newPage (resets to page 0).
      if (
        isBlockEntirelyBeforeCurrentPage(
          startRow,
          endRow,
          pager && pager.currentPage,
          pageSizeForPager
        )
      ) {
        notifyFail(params);
        scheduleCurrentPageNudge(
          params && params.api,
          (pager.currentPage || 0) * pageSizeForPager
        );
        return;
      }

      const cached = blockCache.get(key);
      // Serve only recent successes so SSRM retries of the same block
      // do not refetch, while a later search/refresh still hits the API.
      if (cached && Date.now() - cached.ts < 2000) {
        notifySuccess(params, cached.rowData, cached.total);
        return;
      }
      const existing = inflightBlocks.get(key);
      if (existing) {
        existing.waiters.push(params);
        return;
      }
      const entry = { waiters: [params] };
      inflightBlocks.set(key, entry);

      const mySeq = ++requestSeq;
      const pageSize =
        (getPageSize && getPageSize()) ||
        filterBody.pageSize ||
        (params.request.endRow - params.request.startRow) ||
        10;

      filterBody.pageSize = pageSize;

      if (pageZeroBased) {
        filterBody.pageNo = Math.floor(params.request.startRow / pageSize);
      } else {
        filterBody.pageNo = Math.floor(params.request.startRow / pageSize) + 1;
      }

      const filterModel = params.request && params.request.filterModel;
      if (applyFilterModel) {
        applyFilterModel(filterBody, filterModel || {});
      } else if (mapFilterModel) {
        const mapped = mapFilterModel(filterModel || {});
        if (mapped === '' || mapped == null) {
          delete filterBody.searchObject;
        } else {
          filterBody.searchObject = mapped;
        }
      } else if (filterModel && Object.keys(filterModel).length > 0) {
        filterBody.searchObject = filterModel;
      } else {
        delete filterBody.searchObject;
      }
      applyStoreLocationIdsFromFilterModel(filterBody, filterModel || {});

      // Match pre-migration payloads: omit empty/null searchObject & group_id
      if (
        filterBody.searchObject == null ||
        filterBody.searchObject === '' ||
        (typeof filterBody.searchObject === 'object' &&
          !Array.isArray(filterBody.searchObject) &&
          Object.keys(filterBody.searchObject).length === 0)
      ) {
        delete filterBody.searchObject;
      }
      if (
        filterBody.group_id == null ||
        filterBody.group_id === '' ||
        filterBody.group_id === 'null' ||
        filterBody.group_id === 'undefined'
      ) {
        delete filterBody.group_id;
      }

      if (!filterBody.__defaultSort) {
        filterBody.__defaultSort = {
          sortColumn: filterBody.sortColumn,
          sortOrder: filterBody.sortOrder,
          tableName: filterBody.tableName || '',
        };
      }

      if (params.request.sortModel && params.request.sortModel.length > 0) {
        applySortModel(
          filterBody,
          params.request.sortModel,
          sortMap,
          tableAliases
        );
      } else {
        filterBody.sortColumn = filterBody.__defaultSort.sortColumn;
        filterBody.sortOrder = filterBody.__defaultSort.sortOrder;
        filterBody.tableName = filterBody.__defaultSort.tableName;
      }

      // Clone so internal flags never hit the API.
      const requestBody = { ...filterBody };
      delete requestBody.__dateFilterBound;
      delete requestBody.__dateFilterNeedsReseed;
      delete requestBody.__dateFilterColId;
      delete requestBody.__defaultSort;

      const takeWaiters = () => {
        const entry = inflightBlocks.get(key);
        inflightBlocks.delete(key);
        return (entry && entry.waiters) || [params];
      };

      fetchPageWithRetry(fetchPage, requestBody)
        .then((response) => {
          if (generation !== cacheGeneration) {
            entry.waiters.forEach(notifyFail);
            return;
          }
          // Some Express error paths send a JSON string body.
          if (typeof response === 'string') {
            try {
              response = JSON.parse(response);
            } catch (e) {
              response = { message: response, stack: '' };
            }
          }
          const normalized = normalizeServerSidePage(response);
          const waiters = takeWaiters();

          if (normalized.error) {
            waiters.forEach(notifyFail);
            if (mySeq === requestSeq && onError) onError(normalized.error);
            return;
          }

          const results = normalized.results;
          let total = normalized.total;
          const requestedSize =
            params.request && params.request.endRow != null
              ? Math.max(params.request.endRow - startRow, 1)
              : pageSize;

          // SSRM requires every row from startRow through lastRow in this
          // block. If the API returns fewer rows than it counted (e.g. count
          // query misses group_id), leftover loading cells retry getRows
          // forever. A short page is always the last page.
          if (results.length < requestedSize) {
            total = startRow + results.length;
          } else if (!total || total < startRow + results.length) {
            total = startRow + results.length + 1;
          }

          // Id must match loading-row getRowId (`ssrm-${rowIndex}`). Switching to a
          // business-key id after load leaves AG Grid 29 on Please Wait... and it
          // retries getRows forever.
          const rowData = (results || []).map((row, index) => {
            if (!row || typeof row !== 'object') return row;
            return { ...row, __ssrmId: `ssrm-${startRow + index}` };
          });

          blockCache.set(key, { rowData, total, ts: Date.now() });
          if (blockCache.size > 30) {
            const oldest = blockCache.keys().next().value;
            blockCache.delete(oldest);
          }
          waiters.forEach((p) => notifySuccess(p, rowData, total));

          // Always drop the parent page spinner once a block has rows. A newer
          // in-flight getRows (or a print dialog pausing JS) must not leave
          // the overlay stuck after data is already on screen.
          if (onSuccess) onSuccess(rowData, total);

          // Only the latest request updates AG Grid overlays.
          if (mySeq !== requestSeq) return;

          const api = params.api;
          if (api && isGridApiAlive(api)) {
            if (!total || total === 0 || !rowData.length) {
              api.showNoRowsOverlay();
            } else {
              api.hideOverlay();
            }
          }
        })
        .catch((error) => {
          if (generation !== cacheGeneration) {
            entry.waiters.forEach(notifyFail);
            return;
          }
          console.error(error);
          const waiters = takeWaiters();
          if (mySeq === requestSeq && onError) onError(error);
          if (isGridApiAlive(params.api) && typeof params.api.hideOverlay === 'function') {
            params.api.hideOverlay();
          }
          if (params.api && typeof params.api.showNoRowsOverlay === 'function') {
            params.api.showNoRowsOverlay();
          }
          // After retries exhausted, return empty success so AG Grid settles
          // instead of calling fail() which retriggers getRows forever.
          waiters.forEach((p) => notifySuccess(p, [], 0));
        });
    },
  };
};

export default createServerSideDatasource;



// WEBPACK FOOTER //
// ./src/components/grid/createServerSideDatasource.js